import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { MetricCard } from '../components/MetricCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  BarChart3, 
  FileCheck, 
  Clock, 
  AlertTriangle,
  Users,
  Loader2,
  Calendar,
  TrendingUp,
  RefreshCw,
  Wifi
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

export default function Reports() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [realtimeEvents, setRealtimeEvents] = useState([]);
  const wsRef = useRef(null);

  const formatEmissionDate = (value) => {
    if (!value) return '-';
    const [year, month, day] = String(value).split('-');
    if (year && month && day) return `${day}/${month}/${year}`;
    return value;
  };

  useEffect(() => {
    fetchStats();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const connectWebSocket = () => {
    try {
      const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      wsRef.current = new WebSocket(`${wsUrl}/ws`);
      
      wsRef.current.onopen = () => {
        setWsConnected(true);
        console.log('WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== 'pong') {
            setRealtimeEvents(prev => [data, ...prev].slice(0, 10));
            
            // Auto refresh stats on certain events
            if (['conference_completed', 'invoice_uploaded'].includes(data.type)) {
              fetchStats();
            }
          }
        } catch (e) {
          console.error('WebSocket message error:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        // Reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (e) {
      console.error('WebSocket connection error:', e);
    }
  };

  const fetchStats = async (start, end) => {
    try {
      let url = '/dashboard/stats';
      const params = new URLSearchParams();
      if (start || startDate) params.append('start_date', start || startDate);
      if (end || endDate) params.append('end_date', end || endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setLoading(true);
    fetchStats(startDate, endDate);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0min';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return mins > 0 ? `${mins}min ${secs}s` : `${secs}s`;
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'conference_completed': return '✅';
      case 'conference_started': return '🔄';
      case 'invoice_uploaded': return '📄';
      case 'excess_attempt': return '⚠️';
      case 'item_checked': return '📦';
      default: return '📌';
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 md:ml-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a365d]" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-6 md:p-8 lg:p-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-[#1a365d]" data-testid="reports-title">
              <BarChart3 className="inline mr-3" size={32} />
              Relatórios e Métricas
            </h1>
            <p className="text-slate-500 mt-1">
              Análise de produtividade e desempenho em tempo real
            </p>
          </div>
          
          {/* Real-time indicator */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${wsConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            <Wifi size={16} className={wsConnected ? 'animate-pulse' : ''} />
            <span className="text-sm font-medium">
              {wsConnected ? 'Tempo Real Ativo' : 'Conectando...'}
            </span>
          </div>
        </div>

        {/* Date Filters */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1">
              <Label htmlFor="start-date">Data Início</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
                data-testid="start-date-input"
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="end-date">Data Fim</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
                data-testid="end-date-input"
              />
            </div>
            <Button 
              onClick={handleFilter}
              className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              data-testid="filter-button"
            >
              <Calendar className="mr-2" size={18} />
              Filtrar
            </Button>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            label="Total Conferidas"
            value={stats?.total_invoices_checked || 0}
            icon={FileCheck}
          />
          <MetricCard
            label="Tempo Médio"
            value={formatTime(stats?.average_time_seconds || 0)}
            icon={Clock}
          />
          <MetricCard
            label="Divergências"
            value={stats?.divergence_count || 0}
            icon={AlertTriangle}
          />
          <MetricCard
            label="Operadores Ativos"
            value={stats?.productivity_ranking?.length || 0}
            icon={Users}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Productivity Ranking */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <TrendingUp size={20} className="text-[#1a365d]" />
              Ranking de Produtividade
            </h2>
            
            {stats?.productivity_ranking?.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.productivity_ranking}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="user_name" 
                      width={100}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      formatter={(value, name) => [value, name === 'total_conferences' ? 'Conferências' : 'Tempo Médio']}
                    />
                    <Bar 
                      dataKey="total_conferences" 
                      fill="#1a365d" 
                      radius={[0, 4, 4, 0]}
                      name="Conferências"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-12">
                Nenhum dado disponível para o período selecionado
              </p>
            )}
          </div>

          {/* Real-time Events */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <RefreshCw size={20} className={`text-[#1a365d] ${wsConnected ? 'animate-spin' : ''}`} style={{animationDuration: '3s'}} />
              Eventos em Tempo Real
            </h2>
            
            <div className="space-y-3 max-h-[280px] overflow-y-auto">
              {realtimeEvents.length === 0 ? (
                <p className="text-slate-500 text-center py-8 text-sm">
                  Aguardando eventos...
                </p>
              ) : (
                realtimeEvents.map((event, index) => (
                  <div 
                    key={index} 
                    className="p-3 bg-slate-50 rounded-lg border border-slate-100 animate-fade-in"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getEventIcon(event.type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {event.invoice_number || event.type}
                        </p>
                        <p className="text-xs text-slate-500">
                          {event.user_name && `${event.user_name} • `}
                          {new Date(event.timestamp).toLocaleTimeString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Recent Logs Table */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-heading font-semibold text-slate-800">
              Conferências Recentes
            </h2>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => fetchStats()}
              className="text-slate-500"
            >
              <RefreshCw size={16} className="mr-1" />
              Atualizar
            </Button>
          </div>
          
          <div className={`${startDate || endDate ? 'max-h-[28rem] overflow-auto' : 'overflow-x-auto'}`}>
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">NF</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Emissão</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Usuário</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Tempo</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Divergência</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Máquina</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats?.recent_logs?.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      Nenhuma conferência no período
                    </td>
                  </tr>
                ) : (
                  stats?.recent_logs?.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-mono font-medium text-slate-800">
                        {log.invoice_number}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatEmissionDate(log.emission_date)}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {log.user_name}
                      </td>
                      <td className="px-4 py-3 text-center font-mono text-slate-600">
                        {formatTime(log.total_time_seconds)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          log.had_excess_attempt || log.had_gestor_unlock 
                            ? 'bg-amber-100 text-amber-800' 
                            : 'bg-emerald-100 text-emerald-800'
                        }`}>
                          {log.had_excess_attempt || log.had_gestor_unlock ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-sm">
                        {log.machine_ip}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
